import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Calculator, Home, Percent, HelpCircle } from 'lucide-react';
import { brand, theme } from './theme';

const Tooltip = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState('left');

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Simple positioning: if icon is in right half of screen, position tooltip to the left
    // Otherwise position to the right
    if (rect.left > viewportWidth / 2) {
      setPosition('left');
    } else {
      setPosition('right');
    }
    
    setIsVisible(true);
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div 
          className={`absolute z-50 p-2 sm:p-3 text-xs text-white bg-gray-800 rounded-lg shadow-lg bottom-full mb-2 w-48 sm:w-64
            ${position === 'left' ? 'right-0' : 'left-0'}
          `}
        >
          <div className="relative">
            {content}
            <div 
              className={`absolute top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800
                ${position === 'left' ? 'right-2' : 'left-2'}
              `}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default function RefinanceCalculator() {
  const [currentLoanBalance, setCurrentLoanBalance] = useState(250000);
  const [currentInterestRate, setCurrentInterestRate] = useState(7.0);
  const [currentMonthsRemaining, setCurrentMonthsRemaining] = useState(300);
  const [remainingTimeMode, setRemainingTimeMode] = useState('months');
  const [currentMonthlyPayment, setCurrentMonthlyPayment] = useState(1663);
  const [monthlyPaymentMode, setMonthlyPaymentMode] = useState('calculate');
  const [homeValue, setHomeValue] = useState(350000);
  
  const [newHomeValue, setNewHomeValue] = useState(350000);
  const [newInterestRate, setNewInterestRate] = useState(6.0);
  const [newLoanTerm, setNewLoanTerm] = useState(30);
  const [newLoanType, setNewLoanType] = useState('conventional');
  const [cashOut, setCashOut] = useState(0);
  const [newDownPayment, setNewDownPayment] = useState(0);
  const [closingCosts, setClosingCosts] = useState(6250);
  
  const [currentPMI, setCurrentPMI] = useState(0);
  const [currentPMIMode, setCurrentPMIMode] = useState('estimate');
  
  const [newPmiMode, setNewPmiMode] = useState('estimate');
  const [newManualPMI, setNewManualPMI] = useState(0);
  const [creditScore, setCreditScore] = useState('');
  
  const [prepaymentPenalty, setPrepaymentPenalty] = useState(0);
  const [pointsToBuy, setPointsToBuy] = useState(0);
  const [pointsCost, setPointsCost] = useState(0);
  const [rateReduction, setRateReduction] = useState(0);
  const [closingCostMode, setClosingCostMode] = useState('estimate');
  
  const [results, setResults] = useState({});

  const loanTypes = {
    conventional: { 
      name: 'Conventional', 
      description: 'Standard refinance loan',
      pmiThreshold: 0.20
    },
    fha: { 
      name: 'FHA Streamline', 
      description: 'Simplified FHA refinance',
      pmiThreshold: 1,
      upfrontMIP: 0.0175,
      annualMIP: 0.0055
    },
    va: { 
      name: 'VA IRRRL', 
      description: 'VA Interest Rate Reduction Loan',
      fundingFee: 0.005
    },
    jumbo: { 
      name: 'Jumbo', 
      description: 'High-balance loan refinance',
      pmiThreshold: 0.20
    }
  };

  const formatNumberInput = (value) => {
    if (!value) return '';
    return Number(value).toLocaleString('en-US');
  };

  const parseFormattedNumber = (value) => {
    return Number(String(value).replace(/,/g, ''));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateCurrentPayment = (balance, rate, months) => {
    if (balance <= 0 || rate <= 0 || months <= 0) return 0;
    const monthlyRate = rate / 100 / 12;
    return balance * (monthlyRate * Math.pow(1 + monthlyRate, months)) / 
           (Math.pow(1 + monthlyRate, months) - 1);
  };

  const handleRemainingTimeChange = (value, mode) => {
    const numericValue = Number(value);
    if (mode === 'years') {
      setCurrentMonthsRemaining(numericValue * 12);
    } else {
      setCurrentMonthsRemaining(numericValue);
    }
  };

  const calculatePMIRate = (loanToValue, creditScore) => {
    const pmiRates = {
      excellent: { low: 0.003, medium: 0.004, high: 0.005 },
      good: { low: 0.005, medium: 0.007, high: 0.010 },
      fair: { low: 0.008, medium: 0.011, high: 0.015 },
      poor: { low: 0.012, medium: 0.015, high: 0.020 }
    };

    let scoreCategory;
    if (creditScore >= 740) scoreCategory = 'excellent';
    else if (creditScore >= 680) scoreCategory = 'good';
    else if (creditScore >= 620) scoreCategory = 'fair';
    else scoreCategory = 'poor';

    let ltvCategory;
    if (loanToValue <= 0.80) ltvCategory = 'low';
    else if (loanToValue <= 0.90) ltvCategory = 'medium';
    else ltvCategory = 'high';

    return pmiRates[scoreCategory][ltvCategory];
  };

  const setCurrentPMIModeDirectly = (newMode) => {
    const currentMode = currentPMIMode;
    
    if (currentPMI > 0 && currentMode !== 'estimate' && newMode !== 'estimate') {
      if (newMode === 'monthly' && currentMode === 'annual') {
        setCurrentPMI(Math.round((currentPMI / 12) * 100) / 100);
      } else if (newMode === 'annual' && currentMode === 'monthly') {
        setCurrentPMI(Math.round((currentPMI * 12) * 100) / 100);
      }
    }
    
    setCurrentPMIMode(newMode);
    
    if (newMode === 'estimate') {
      const currentLTV = currentLoanBalance / homeValue;
      let estimatedPMI = 0;
      if (currentLTV > 0.80) {
        if (creditScore && creditScore !== '') {
          const score = parseInt(creditScore);
          const pmiRate = calculatePMIRate(currentLTV, score);
          estimatedPMI = Math.round(currentLoanBalance * pmiRate);
        } else {
          estimatedPMI = Math.round(currentLoanBalance * 0.005);
        }
      }
      setCurrentPMI(estimatedPMI);
    }
  };

  const setNewPmiModeDirectly = (newMode) => {
    const currentMode = newPmiMode;
    
    if (newManualPMI > 0 && currentMode !== 'estimate' && newMode !== 'estimate') {
      if (newMode === 'monthly' && currentMode === 'annual') {
        setNewManualPMI(Math.round((newManualPMI / 12) * 100) / 100);
      } else if (newMode === 'annual' && currentMode === 'monthly') {
        setNewManualPMI(Math.round((newManualPMI * 12) * 100) / 100);
      }
    }
    
    setNewPmiMode(newMode);
    
    if (newMode === 'estimate') {
      setNewManualPMI(0);
    }
  };

  const getSavingsColor = (amount) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const calculateRefinance = () => {
    const newLoanAmount = currentLoanBalance + cashOut - newDownPayment;
    const effectiveNewRate = newInterestRate - rateReduction;
    const newMonthlyRate = effectiveNewRate / 100 / 12;
    const newNumPayments = newLoanTerm * 12;
    const loanConfig = loanTypes[newLoanType];
    
    const newMonthlyPayment = newLoanAmount * 
      (newMonthlyRate * Math.pow(1 + newMonthlyRate, newNumPayments)) / 
      (Math.pow(1 + newMonthlyRate, newNumPayments) - 1);
    
    const currentPayment = monthlyPaymentMode === 'calculate'
      ? calculateCurrentPayment(currentLoanBalance, currentInterestRate, currentMonthsRemaining)
      : currentMonthlyPayment;
    
    let currentMonthlyPMI = 0;
    const currentLTV = currentLoanBalance / homeValue;
    if (currentLTV > 0.80) {
      if (currentPMIMode === 'estimate') {
        if (creditScore && creditScore !== '') {
          const score = parseInt(creditScore);
          const pmiRate = calculatePMIRate(currentLTV, score);
          currentMonthlyPMI = (currentLoanBalance * pmiRate) / 12;
        } else {
          currentMonthlyPMI = (currentLoanBalance * 0.005) / 12;
        }
      } else {
        currentMonthlyPMI = currentPMIMode === 'monthly' ? currentPMI : currentPMI / 12;
      }
    }
    
    let newMonthlyPMI = 0;
    let upfrontFee = 0;
    let fundingFee = 0;
    
    const loanToValue = newLoanAmount / newHomeValue;
    
    if (newLoanType === 'conventional' || newLoanType === 'jumbo') {
      if (loanToValue > 0.80) {
        if (newPmiMode === 'estimate') {
          if (creditScore && creditScore !== '') {
            const score = parseInt(creditScore);
            const pmiRate = calculatePMIRate(loanToValue, score);
            newMonthlyPMI = (newLoanAmount * pmiRate) / 12;
          } else {
            newMonthlyPMI = (newLoanAmount * 0.005) / 12;
          }
        } else {
          newMonthlyPMI = newPmiMode === 'monthly' ? Number(newManualPMI) : Number(newManualPMI) / 12;
        }
      }
    } else if (newLoanType === 'fha') {
      upfrontFee = newLoanAmount * loanConfig.upfrontMIP;
      if (newPmiMode === 'estimate') {
        newMonthlyPMI = (newLoanAmount * loanConfig.annualMIP) / 12;
      } else {
        newMonthlyPMI = newPmiMode === 'monthly' ? Number(newManualPMI) : Number(newManualPMI) / 12;
      }
    } else if (newLoanType === 'va') {
      fundingFee = newLoanAmount * loanConfig.fundingFee;
    }
    
    const currentTotalPayment = currentPayment + currentMonthlyPMI;
    const newTotalPayment = newMonthlyPayment + newMonthlyPMI;
    
    const monthlySavings = currentTotalPayment - newTotalPayment;
    const pmiSavings = currentMonthlyPMI - newMonthlyPMI;
    const totalUpfrontCosts = closingCosts + upfrontFee + fundingFee + pointsCost + prepaymentPenalty + newDownPayment;
    const netCashAtClosing = cashOut - totalUpfrontCosts;
    const breakEvenMonths = monthlySavings > 0 ? totalUpfrontCosts / monthlySavings : 0;
    
    setResults({
      currentPayment: currentTotalPayment,
      currentPaymentPI: currentPayment,
      currentMonthlyPMI,
      newMonthlyPayment: newTotalPayment,
      newPaymentPI: newMonthlyPayment,
      newMonthlyPMI,
      newLoanAmount,
      monthlySavings,
      pmiSavings,
      upfrontFee,
      fundingFee,
      totalUpfrontCosts,
      breakEvenMonths,
      loanToValue,
      firstYearSavings: (monthlySavings * 12) - totalUpfrontCosts,
      threeYearSavings: (monthlySavings * 36) - totalUpfrontCosts,
      fiveYearSavings: (monthlySavings * 60) - totalUpfrontCosts,
      effectiveNewRate,
      pointsCost,
      prepaymentPenalty,
      cashOut,
      newDownPayment,
      netCashAtClosing
    });
  };

  useEffect(() => {
    if (monthlyPaymentMode === 'calculate') {
      const calculatedPayment = calculateCurrentPayment(currentLoanBalance, currentInterestRate, currentMonthsRemaining);
      setCurrentMonthlyPayment(Math.round(calculatedPayment));
    }
  }, [currentLoanBalance, currentInterestRate, currentMonthsRemaining, monthlyPaymentMode]);

  useEffect(() => {
    if (pointsToBuy > 0) {
      const newLoanAmount = currentLoanBalance + cashOut - newDownPayment;
      const cost = newLoanAmount * (pointsToBuy / 100);
      const reduction = pointsToBuy * 0.25;
      setPointsCost(Math.round(cost));
      setRateReduction(reduction);
    } else {
      setPointsCost(0);
      setRateReduction(0);
    }
  }, [pointsToBuy, currentLoanBalance, cashOut, newDownPayment]);

  useEffect(() => {
    if (closingCostMode === 'estimate') {
      const estimatedCosts = newHomeValue * 0.025;
      setClosingCosts(Math.round(estimatedCosts));
    }
  }, [closingCostMode, newHomeValue]);

  useEffect(() => {
    const currentLTV = currentLoanBalance / homeValue;
    
    if (currentPMIMode === 'estimate') {
      let estimatedPMI = 0;
      if (currentLTV > 0.80) {
        if (creditScore && creditScore !== '') {
          const score = parseInt(creditScore);
          const pmiRate = calculatePMIRate(currentLTV, score);
          estimatedPMI = Math.round(currentLoanBalance * pmiRate);
        } else {
          estimatedPMI = Math.round(currentLoanBalance * 0.005);
        }
      }
      setCurrentPMI(estimatedPMI);
    }
  }, [currentLoanBalance, homeValue, currentPMIMode, creditScore]);

  useEffect(() => {
    calculateRefinance();
  }, [currentLoanBalance, currentInterestRate, currentMonthsRemaining, currentMonthlyPayment,
      newInterestRate, newLoanTerm, newLoanType, cashOut, newDownPayment, 
      homeValue, newHomeValue, closingCosts, monthlyPaymentMode,
      currentPMI, currentPMIMode, newPmiMode, newManualPMI,
      creditScore, prepaymentPenalty, pointsCost, rateReduction, closingCostMode]);

  return (
    <div className={`max-w-7xl mx-auto p-2 sm:p-4 ${theme.pageBg} min-h-screen`}>
      <div className="bg-white rounded-xl shadow-xl overflow-hidden">
        <div className={`${theme.headerBar} text-white p-4 sm:p-6`}>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              {brand.name && (
                <div className="text-white/90 font-bold text-lg sm:text-xl tracking-wide">{brand.name}</div>
              )}
              <h1 className="text-base sm:text-lg font-bold text-white/95">Refinance Calculator</h1>
            </div>
          </div>
        </div>

        <div className="p-3 sm:p-6">
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
            
            {/* Current Loan Section */}
            <div className={`${theme.panelSoft} rounded-lg p-3 sm:p-4`}>
              <h3 className={`text-sm sm:text-md font-semibold ${theme.headingAccent} mb-3 sm:mb-4 flex items-center gap-2`}>
                <Home className="w-4 h-4" />
                Current Loan
              </h3>
              
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-xs font-semibold text-gray-800">Original Loan Amount</label>
                    <Tooltip content="The total amount you originally borrowed when you first got your mortgage.">
                      <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                    <input
                      type="text"
                      value={formatNumberInput(homeValue)}
                      onChange={(e) => setHomeValue(parseFormattedNumber(e.target.value))}
                      className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                      placeholder="300,000"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-xs font-semibold text-gray-800">Loan Balance</label>
                    <Tooltip content="The amount you currently owe on your mortgage. This appears on your monthly mortgage statement.">
                      <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                    <input
                      type="text"
                      value={formatNumberInput(currentLoanBalance)}
                      onChange={(e) => setCurrentLoanBalance(parseFormattedNumber(e.target.value))}
                      className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                      placeholder="250,000"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Current LTV: {((currentLoanBalance / homeValue) * 100).toFixed(1)}%
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="flex items-center justify-between mb-1 h-6">
                      <div className="flex items-center gap-1">
                        <label className="text-xs font-semibold text-gray-800">Rate</label>
                        <Tooltip content="Your current mortgage interest rate as an annual percentage.">
                          <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                        </Tooltip>
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={currentInterestRate}
                        onChange={(e) => setCurrentInterestRate(Number(e.target.value))}
                        className={`w-full pl-2 pr-6 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                        placeholder="7.0"
                      />
                      <Percent className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1 h-6">
                      <label className="text-xs font-semibold text-gray-800 whitespace-nowrap">Time Left</label>
                      <div className="flex gap-0.5 bg-gray-100 rounded p-0.5 ml-1">
                        <button
                          type="button"
                          onClick={() => setRemainingTimeMode('years')}
                          className={`px-1.5 sm:px-2 py-0.5 text-xs rounded ${
                            remainingTimeMode === 'years' ? theme.toggleActive : 'text-gray-600'
                          }`}
                        >
                          <span className="sm:hidden">Y</span>
                          <span className="hidden sm:inline">Years</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setRemainingTimeMode('months')}
                          className={`px-1.5 sm:px-2 py-0.5 text-xs rounded ${
                            remainingTimeMode === 'months' ? theme.toggleActive : 'text-gray-600'
                          }`}
                        >
                          <span className="sm:hidden">M</span>
                          <span className="hidden sm:inline">Months</span>
                        </button>
                      </div>
                    </div>
                    <input
                      type="number"
                      value={remainingTimeMode === 'years' ? Math.round(currentMonthsRemaining / 12) : currentMonthsRemaining}
                      onChange={(e) => handleRemainingTimeChange(e.target.value, remainingTimeMode)}
                      className={`w-full px-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                      placeholder={remainingTimeMode === 'years' ? '25' : '300'}
                    />
                  </div>
                </div>

                {((currentLoanBalance / homeValue) > 0.80) && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1">
                        <label className="text-xs font-semibold text-gray-800">PMI</label>
                        <Tooltip content="Private Mortgage Insurance (PMI) is required when you owe more than 80% of your home's original value. It protects the lender if you default. PMI can typically be removed once you owe 80% or less of the original home value, either through payments or home appreciation.">
                          <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                        </Tooltip>
                      </div>
                      <div className="flex gap-0.5 bg-gray-100 rounded p-0.5">
                        {['estimate', 'annual', 'monthly'].map((mode) => (
                          <button
                            key={mode}
                            onClick={() => setCurrentPMIModeDirectly(mode)}
                            className={`px-1 sm:px-1.5 py-0.5 text-xs rounded capitalize ${
                              currentPMIMode === mode ? theme.toggleActive : 'text-gray-600'
                            }`}
                          >
                            {mode === 'estimate' ? 'Est' : mode === 'annual' ? 'Ann' : 'Mon'}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {currentPMIMode === 'estimate' && (
                      <div className="mb-2">
                        <div className="flex items-center gap-1 mb-1">
                          <label className="text-xs font-semibold text-gray-800">Credit Score (Optional)</label>
                          <Tooltip content={
                            <div>
                              <strong>How Credit Score Affects PMI</strong>
                              <br />• <strong>740+:</strong> Excellent - Lowest PMI rates (0.3% - 0.5%)
                              <br />• <strong>680-739:</strong> Good - Moderate PMI rates (0.5% - 1.0%)
                              <br />• <strong>620-679:</strong> Fair - Higher PMI rates (0.8% - 1.5%)
                              <br />• <strong>Below 620:</strong> Poor - Highest PMI rates (1.2% - 2.0%)
                              <br /><br />Higher credit scores result in significantly lower monthly PMI costs.
                            </div>
                          }>
                            <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                        <input
                          type="number"
                          value={creditScore}
                          onChange={(e) => setCreditScore(e.target.value)}
                          placeholder="720"
                          min="300"
                          max="850"
                          className={`w-full px-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                        />
                      </div>
                    )}
                    
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                      <input
                        type="text"
                        value={formatNumberInput(currentPMI)}
                        onChange={(e) => setCurrentPMI(parseFormattedNumber(e.target.value))}
                        disabled={currentPMIMode === 'estimate'}
                        className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1} ${
                          currentPMIMode === 'estimate' ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                        placeholder={currentPMIMode === 'annual' ? '1,500' : '125'}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {currentPMIMode === 'estimate' 
                        ? (creditScore ? 
                           `Estimated annual PMI based on ${creditScore} credit score and ${((currentLoanBalance / homeValue) * 100).toFixed(1)}% LTV (${formatCurrency(currentPMI / 12)}/month)` :
                           `Estimated annual PMI at 0.5% rate (${formatCurrency(currentPMI / 12)}/month)`
                          )
                        : currentPMI > 0 ? (
                          currentPMIMode === 'annual' 
                            ? `Monthly equivalent: ${formatCurrency(currentPMI / 12)}`
                            : `Annual equivalent: ${formatCurrency(currentPMI * 12)}`
                        ) : (
                          `Enter ${currentPMIMode} PMI amount`
                        )
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* New Loan Section */}
            <div className={`${theme.panelStrong} rounded-lg p-3 sm:p-4`}>
              <h3 className={`text-sm sm:text-md font-semibold ${theme.headingAccent} mb-3 sm:mb-4 flex items-center gap-2`}>
                <Calculator className="w-4 h-4" />
                New Loan
              </h3>
              
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <label className="text-xs font-semibold text-gray-800">Loan Type</label>
                    <Tooltip content="Different loan types have different requirements and benefits. Conventional loans are most common, FHA has lower down payment requirements, VA loans are for veterans, and Jumbo loans are for expensive homes.">
                      <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                  <select
                    value={newLoanType}
                    onChange={(e) => setNewLoanType(e.target.value)}
                    className={`w-full px-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                  >
                    {Object.entries(loanTypes).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-xs font-semibold text-gray-800">Appraisal Value</label>
                    <Tooltip content="The current estimated value of your home. This will be determined by a professional appraisal during the refinance process.">
                      <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                    <input
                      type="text"
                      value={formatNumberInput(newHomeValue)}
                      onChange={(e) => setNewHomeValue(parseFormattedNumber(e.target.value))}
                      className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                      placeholder="350,000"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                    <label className="text-xs font-semibold text-gray-800">Rate</label>
                      <Tooltip content="The new interest rate you expect to get with your refinance.">
                        <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={newInterestRate}
                        onChange={(e) => setNewInterestRate(Number(e.target.value))}
                        className={`w-full pl-2 pr-6 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                        placeholder="6.0"
                      />
                      <Percent className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="text-xs font-semibold text-gray-800">Term</label>
                      <Tooltip content="The length of your new loan. Shorter terms have higher monthly payments but pay less interest over time. 30 years is most common.">
                        <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <select
                      value={newLoanTerm}
                      onChange={(e) => setNewLoanTerm(Number(e.target.value))}
                      className={`w-full px-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                    >
                      <option value={15}>15yr</option>
                      <option value={20}>20yr</option>
                      <option value={25}>25yr</option>
                      <option value={30}>30yr</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="text-xs font-semibold text-gray-800">Cash Out</label>
                      <Tooltip content="Extra money you want to borrow beyond your current balance. This increases your loan amount but gives you cash for home improvements, debt consolidation, or other needs.">
                        <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                      <input
                        type="text"
                        value={formatNumberInput(cashOut)}
                        onChange={(e) => setCashOut(parseFormattedNumber(e.target.value))}
                        className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="text-xs font-semibold text-gray-800">Extra Down</label>
                      <Tooltip content="Additional money you want to put toward the loan to reduce the balance. This lowers your new loan amount and monthly payment.">
                        <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                      <input
                        type="text"
                        value={formatNumberInput(newDownPayment)}
                        onChange={(e) => setNewDownPayment(parseFormattedNumber(e.target.value))}
                        className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <label className="text-xs font-semibold text-gray-800">Closing Costs</label>
                      <Tooltip content="Fees to complete the refinance including appraisal, title insurance, attorney fees, and lender charges. Typically 2-5% of loan amount.">
                        <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="flex gap-0.5 bg-gray-100 rounded p-0.5">
                      <button
                        type="button"
                        onClick={() => setClosingCostMode('estimate')}
                        className={`px-1.5 py-0.5 text-xs rounded ${
                          closingCostMode === 'estimate' ? theme.toggleActive : 'text-gray-600'
                        }`}
                      >
                        Estimate
                      </button>
                      <button
                        type="button"
                        onClick={() => setClosingCostMode('manual')}
                        className={`px-1.5 py-0.5 text-xs rounded ${
                          closingCostMode === 'manual' ? theme.toggleActive : 'text-gray-600'
                        }`}
                      >
                        Manual
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                    <input
                      type="text"
                      value={formatNumberInput(closingCosts)}
                      onChange={(e) => setClosingCosts(parseFormattedNumber(e.target.value))}
                      disabled={closingCostMode === 'estimate'}
                      className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1} ${
                        closingCostMode === 'estimate' ? 'bg-gray-100 text-gray-600' : ''
                      }`}
                      placeholder="6,250"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {closingCostMode === 'estimate' 
                      ? 'Estimated at 2.5% of appraisal value'
                      : 'Enter your closing cost estimate'
                    }
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="text-xs font-semibold text-gray-800">Prepay Penalty</label>
                      <Tooltip content="A fee your current lender may charge for paying off your loan early. Not all loans have this penalty.">
                        <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                      <input
                        type="text"
                        value={formatNumberInput(prepaymentPenalty)}
                        onChange={(e) => setPrepaymentPenalty(parseFormattedNumber(e.target.value))}
                        className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="text-xs font-semibold text-gray-800">Points</label>
                      <Tooltip content="Optional upfront payment to reduce your interest rate. Each point costs 1% of your loan amount and typically reduces your rate by 0.25%. Only worth it if you plan to stay in the home long-term.">
                        <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <input
                      type="number"
                      step="0.25"
                      value={pointsToBuy}
                      onChange={(e) => setPointsToBuy(Number(e.target.value))}
                      className={`w-full px-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
                      placeholder="0"
                      min="0"
                      max="4"
                    />
                  </div>
                </div>

                {((newLoanType === 'conventional' || newLoanType === 'jumbo') && (results.loanToValue > 0.80)) || 
                 newLoanType === 'fha' || newLoanType === 'usda' ? (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1">
                        <label className="text-xs font-semibold text-gray-800">
                          {newLoanType === 'fha' ? 'MIP' : newLoanType === 'usda' ? 'Guarantee Fee' : 'PMI'}
                        </label>
                        <Tooltip content={
                          newLoanType === 'fha' 
                            ? "FHA Mortgage Insurance Premium is required on all FHA loans."
                            : newLoanType === 'usda'
                            ? "USDA Guarantee Fee is required on all USDA loans."
                            : "PMI is required when the new loan-to-value is above 80%."
                        }>
                          <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                        </Tooltip>
                      </div>
                      <div className="flex gap-0.5 bg-gray-100 rounded p-0.5">
                        {['estimate', 'annual', 'monthly'].map((mode) => (
                          <button
                            key={mode}
                            onClick={() => setNewPmiModeDirectly(mode)}
                            className={`px-1 sm:px-1.5 py-0.5 text-xs rounded capitalize ${
                              newPmiMode === mode ? theme.toggleActive : 'text-gray-600'
                            }`}
                          >
                            {mode === 'estimate' ? 'Est' : mode === 'annual' ? 'Ann' : 'Mon'}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {newPmiMode === 'estimate' && (newLoanType === 'conventional' || newLoanType === 'jumbo') && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-600">
                          {creditScore ? 
                            `Using credit score ${creditScore} for PMI calculation` :
                            `Enter credit score above for more accurate PMI estimate`
                          }
                        </p>
                      </div>
                    )}

                    {newPmiMode === 'estimate' && newLoanType === 'fha' && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-600">
                          FHA MIP: 1.75% upfront + 0.55% annual
                        </p>
                      </div>
                    )}

                    {newPmiMode === 'estimate' && newLoanType === 'usda' && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-600">
                          USDA fees: 1% upfront + 0.35% annual
                        </p>
                      </div>
                    )}
                    
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                      <input
                        type="text"
                        value={newPmiMode === 'estimate' 
                          ? formatNumberInput(
                              newLoanType === 'fha' 
                                ? Math.round((currentLoanBalance + cashOut - newDownPayment) * 0.0055)
                                : newLoanType === 'usda'
                                ? Math.round((currentLoanBalance + cashOut - newDownPayment) * 0.0035)
                                : creditScore && creditScore !== ''
                                ? Math.round((currentLoanBalance + cashOut - newDownPayment) * calculatePMIRate(results.loanToValue || 0.85, parseInt(creditScore)))
                                : Math.round((currentLoanBalance + cashOut - newDownPayment) * 0.005)
                            )
                          : formatNumberInput(newManualPMI)
                        }
                        onChange={(e) => setNewManualPMI(parseFormattedNumber(e.target.value))}
                        disabled={newPmiMode === 'estimate'}
                        className={`w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1} ${
                          newPmiMode === 'estimate' ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                        placeholder={newPmiMode === 'annual' ? '1,500' : '125'}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {newPmiMode === 'estimate' 
                        ? `Estimated ${newPmiMode === 'annual' ? 'annual' : 'annual'} amount (${
                            formatCurrency(
                              newLoanType === 'fha'
                                ? Math.round((currentLoanBalance + cashOut - newDownPayment) * 0.0055 / 12)
                                : newLoanType === 'usda'
                                ? Math.round((currentLoanBalance + cashOut - newDownPayment) * 0.0035 / 12)
                                : creditScore && creditScore !== ''
                                ? Math.round((currentLoanBalance + cashOut - newDownPayment) * calculatePMIRate(results.loanToValue || 0.85, parseInt(creditScore)) / 12)
                                : Math.round((currentLoanBalance + cashOut - newDownPayment) * 0.005 / 12)
                            )
                          }/month)`
                        : newManualPMI > 0 ? (
                          newPmiMode === 'annual' 
                            ? `Monthly equivalent: ${formatCurrency(newManualPMI / 12)}`
                            : `Annual equivalent: ${formatCurrency(newManualPMI * 12)}`
                        ) : (
                          `Enter ${newPmiMode} ${newLoanType === 'fha' ? 'MIP' : newLoanType === 'usda' ? 'guarantee fee' : 'PMI'} amount`
                        )
                      }
                    </p>
                  </div>
                ) : null}

                <div className="bg-white/50 rounded p-2 text-xs">
                  <div className="flex justify-between">
                    <span>Loan Amount:</span>
                    <span className="font-medium">{formatCurrency(results.newLoanAmount || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>New LTV:</span>
                    <span className="font-medium">{(results.loanToValue * 100 || 0).toFixed(1)}%</span>
                  </div>
                  {pointsToBuy > 0 && (
                    <div className="flex justify-between">
                      <span>Effective Rate:</span>
                      <span className="font-medium text-green-600">{(newInterestRate - rateReduction).toFixed(3)}%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className={`${theme.panelHighlight} rounded-lg p-3 sm:p-4`}>
              <h3 className={`text-sm sm:text-md font-semibold ${theme.headingAccent} mb-3 sm:mb-4 flex items-center gap-2`}>
                <TrendingUp className="w-4 h-4" />
                Analysis
              </h3>
              
              <div className="space-y-3">
                {/* Monthly Payments Box */}
                <div className={`bg-white rounded p-3 border ${theme.borderAccent}`}>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Monthly Payments</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span>Current Total:</span>
                      <span className="font-medium">{formatCurrency(results.currentPayment || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>New Total:</span>
                      <span className="font-medium">{formatCurrency(results.newMonthlyPayment || 0)}</span>
                    </div>
                    <div className={`flex justify-between pt-1 border-t font-semibold ${getSavingsColor(results.monthlySavings)}`}>
                      <span>Monthly Savings:</span>
                      <span>{results.monthlySavings > 0 ? '+' : ''}{formatCurrency(results.monthlySavings || 0)}</span>
                    </div>
                  </div>
                </div>

                {/* Costs & Savings Box */}
                <div className={`bg-white rounded p-3 border ${theme.borderAccent}`}>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Costs & Savings</h4>
                  <div className="space-y-2">
                    {/* Upfront Costs Section */}
                    <div className="space-y-1 text-xs pb-2 border-b">
                      <div className="flex justify-between">
                        <span>Total Upfront Costs:</span>
                        <span className="font-medium text-red-600">{formatCurrency(results.totalUpfrontCosts || 0)}</span>
                      </div>
                      <div className="text-gray-500 pl-2 space-y-0.5">
                        <div className="flex justify-between">
                          <span>• Closing:</span>
                          <span>{formatCurrency(closingCosts)}</span>
                        </div>
                        {results.newDownPayment > 0 && (
                          <div className="flex justify-between">
                            <span>• Extra Down:</span>
                            <span>{formatCurrency(results.newDownPayment)}</span>
                          </div>
                        )}
                        {results.pointsCost > 0 && (
                          <div className="flex justify-between">
                            <span>• Points:</span>
                            <span>{formatCurrency(results.pointsCost)}</span>
                          </div>
                        )}
                        {prepaymentPenalty > 0 && (
                          <div className="flex justify-between">
                            <span>• Penalty:</span>
                            <span>{formatCurrency(prepaymentPenalty)}</span>
                          </div>
                        )}
                        {results.upfrontFee > 0 && (
                          <div className="flex justify-between">
                            <span>• {newLoanType === 'fha' ? 'MIP' : newLoanType === 'va' ? 'Funding' : 'Fee'}:</span>
                            <span>{formatCurrency(results.upfrontFee)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Cash Out Section */}
                    {results.cashOut > 0 && (
                      <div className="space-y-1 text-xs pb-2 border-b">
                        <div className="flex justify-between">
                          <span>Cash Out:</span>
                          <span className="font-medium text-green-600">+{formatCurrency(results.cashOut)}</span>
                        </div>
                        <div className={`flex justify-between font-medium ${results.netCashAtClosing >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          <span>Net Cash at Closing:</span>
                          <span>{results.netCashAtClosing >= 0 ? '+' : ''}{formatCurrency(results.netCashAtClosing || 0)}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Break-Even & Savings Timeline */}
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between font-medium">
                        <span>Break-Even Period:</span>
                        <span className="text-blue-600">
                          {results.breakEvenMonths > 0 ? `${Math.round(results.breakEvenMonths)} months` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Year 1 Net:</span>
                        <span className={`font-medium ${getSavingsColor(results.firstYearSavings)}`}>
                          {results.firstYearSavings > 0 ? '+' : ''}{formatCurrency(results.firstYearSavings || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Year 3 Net:</span>
                        <span className={`font-medium ${getSavingsColor(results.threeYearSavings)}`}>
                          {results.threeYearSavings > 0 ? '+' : ''}{formatCurrency(results.threeYearSavings || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Year 5 Net:</span>
                        <span className={`font-medium ${getSavingsColor(results.fiveYearSavings)}`}>
                          {results.fiveYearSavings > 0 ? '+' : ''}{formatCurrency(results.fiveYearSavings || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Metrics Box */}
                <div className={`bg-white rounded p-3 border ${theme.borderAccent}`}>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Key Metrics</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span>Rate Change:</span>
                      <span className={`font-medium ${getSavingsColor(currentInterestRate - newInterestRate)}`}>
                        {currentInterestRate > newInterestRate ? '-' : '+'}{Math.abs(currentInterestRate - newInterestRate).toFixed(3)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Term Change:</span>
                      <span className="font-medium">
                        {Math.round(currentMonthsRemaining / 12)}yr → {newLoanTerm}yr
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>LTV Change:</span>
                      <span className="font-medium">
                        {((currentLoanBalance / homeValue) * 100).toFixed(1)}% → {(results.loanToValue * 100 || 0).toFixed(1)}%
                      </span>
                    </div>
                    {results.pmiSavings !== 0 && (
                      <div className="flex justify-between">
                        <span>
                          {newLoanType === 'fha' ? 'MIP' : newLoanType === 'usda' ? 'Guarantee Fee' : 'PMI'} Change:
                        </span>
                        <span className={`font-medium ${getSavingsColor(results.pmiSavings)}`}>
                          {results.pmiSavings > 0 ? '+' : ''}{formatCurrency(results.pmiSavings)}/mo
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-center">
            <button
              onClick={() => {
                setCurrentLoanBalance(250000);
                setCurrentInterestRate(7.0);
                setNewInterestRate(6.0);
                setHomeValue(350000);
                setNewHomeValue(350000);
                setCashOut(0);
                setNewDownPayment(0);
                setClosingCosts(6250);
                setCreditScore('');
                setPrepaymentPenalty(0);
                setPointsToBuy(0);
              }}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
            >
              Reset Calculator
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}